import { useRef, type ReactNode } from "react";
import { ScrollView } from "react-native";
import LBox from "@components/LBox";
import LText from "@components/LText";
import LButton from "@components/LButton";
import { PURCHASED_TICKETS_COPY, ticketCounter } from "@libs/shared-ui";

export type TicketCarouselPage = {
  key: string;
  /** What sits in the white box: a QR code, a waitlist notice, a code. */
  box: ReactNode;
  /** Under "Ticket n of N": a label, the last four digits. */
  caption?: ReactNode;
};

/**
 * The ticket pages of My Tickets: one white box per ticket, paged
 * horizontally with previous/next buttons and "Ticket n of N". Shared by
 * engine tickets (their QR codes) and Skiddle's own codes.
 */
export function TicketCarousel({
  pages,
  width,
  boxWidth,
}: {
  pages: TicketCarouselPage[];
  /** One page: exactly the carousel's width, so paging lands on a ticket. */
  width: number;
  boxWidth: number;
}) {
  const scrollRef = useRef<ScrollView>(null);
  const scrollToTicket = (index: number) => {
    scrollRef.current?.scrollTo({ x: index * width, animated: true });
  };

  return (
    <ScrollView
      ref={scrollRef}
      testID="view-tickets-carousel"
      horizontal
      // Each page is exactly the carousel's width, so paging lands a
      // swipe on one whole ticket. Unlike `snapToInterval`, paging also
      // snaps on web (CSS scroll-snap in react-native-web).
      pagingEnabled
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      bounces={false}
      // No `flex` on the row or its pages: `flex: 1` sets a zero flex
      // basis, which overrides `width` and squeezes every ticket into
      // one screen width, leaving nothing to scroll (LIS-984).
      contentContainerStyle={{ alignItems: "center" }}
    >
      {pages.map((page, i) => (
        <LBox key={page.key} width={width} testID={`view-tickets-page-${i}`}>
          <LBox alignItems="center" justifyContent="center">
            {page.box}
            <LBox
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <LBox alignItems="center" justifyContent="center" flex={1}>
                <LButton
                  sentryEventName="show_ticket_modal_previous_ticket"
                  variant="roundSmall"
                  label={PURCHASED_TICKETS_COPY.previous}
                  onPress={() => scrollToTicket(i - 1)}
                  enabled={i !== 0}
                />
              </LBox>
              <LBox width={boxWidth} height="100%" />
              <LBox alignItems="center" justifyContent="center" flex={1}>
                <LButton
                  sentryEventName="show_ticket_modal_next_ticket"
                  variant="roundSmall"
                  label={PURCHASED_TICKETS_COPY.next}
                  onPress={() => scrollToTicket(i + 1)}
                  enabled={i !== pages.length - 1}
                />
              </LBox>
            </LBox>
          </LBox>

          <LBox alignItems="center" justifyContent="center" py="s2">
            <LText variant="bodyM" color="white">
              {ticketCounter(i, pages.length)}
            </LText>
            {page.caption}
          </LBox>
        </LBox>
      ))}
    </ScrollView>
  );
}
